import { useState } from 'react';
import { ArrowRight, TreePine, Building, Sun, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import RegistrationModal from '@/components/RegistrationModal';
import { useGateAction } from '@/hooks/useGateAction';
import { GATE_SOURCES } from '@/lib/gateActions';
import PlanExplainerModal from '@/components/PlanExplainerModal';

const usps = [
  { icon: TreePine, label: 'Private Miyawaki Forest' },
  { icon: Building, label: 'Only 4 Residences Per Floor' },
  { icon: Sun, label: 'Expansive Sun Decks' },
];

const stats = [
  { label: 'Possession', value: 'Dec 2030' },
  { label: 'Carpet', value: '886 – 2,487 sq.ft.' },
  { label: 'Location', value: 'Chandivali – Powai' },
  { label: 'Starting', value: '₹2.90 Cr*' },
  { label: 'Payment Plan', value: '30:70 Subvention' },
];

const Hero = () => {
  const tourGate = useGateAction({
    cta: 'hero_cta',
    source: GATE_SOURCES.hero_enquiry,
    modalType: 'contact',
  });

  const [planModalOpen, setPlanModalOpen] = useState(false);

  return (
    <section className="relative isolate min-h-[100svh] min-h-screen flex flex-col overflow-hidden bg-black">
      {/* Background Image */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <img
          src="/hero/hero-3.jpg"
          alt="View from a luxury deck overlooking Miyawaki forest canopy with Mumbai skyline at golden hour"
          className="w-full h-full object-cover"
          style={{ objectPosition: '60% center' }}
          loading="eager"
          fetchPriority="high"
          decoding="sync"
          width={1920}
          height={1080}
        />
        {/* Desktop: Horizontal cinematic vignette - dark left, clear right */}
        <div
          className="absolute inset-0 z-10 hidden md:block"
          style={{
            background: 'linear-gradient(to right, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.55) 30%, rgba(0,0,0,0.15) 55%, transparent 70%)',
          }}
        />
        {/* Mobile: Vertical vignette - dark top, lighter bottom */}
        <div
          className="absolute inset-0 z-10 md:hidden"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0.35) 70%, rgba(0,0,0,0.2) 100%)',
          }}
        />
      </div>

      {/* Main Content - Vertically centered */}
      <div className="relative z-30 flex-1 flex items-center">
        <div className="container mx-auto px-4 pt-36 pb-32 md:pt-40 md:pb-40">
          <div className="max-w-3xl">
            {/* Headline - Playfair Display Serif */}
            <h1
              className="text-primary-foreground mb-5 animate-fade-in-up"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(2.4rem, 5.5vw, 5rem)',
                fontWeight: 600,
                lineHeight: 1.05,
                letterSpacing: '-0.02em',
                animationDelay: '100ms',
                textShadow: '0 4px 24px rgba(0,0,0,0.6), 0 1px 4px rgba(0,0,0,0.4)',
              }}
            >
              Live in Mumbai's<br />Secret Forest.
            </h1>

            {/* Sub-headline - DM Sans */}
            <p
              className="text-primary-foreground/90 mb-10 max-w-xl animate-fade-in-up"
              style={{
                fontFamily: "'DM Sans', 'Inter', sans-serif",
                fontSize: 'clamp(1rem, 2vw, 1.25rem)',
                lineHeight: 1.6,
                fontWeight: 500,
                animationDelay: '200ms',
                textShadow: '0 2px 12px rgba(0,0,0,0.5)',
              }}
            >
              Exclusive 3 &amp; 4 BHK deck residences overlooking 8 acres of private Miyawaki forest. Pay just 30% now and the balance 70% only on possession — under Nahar's launch-phase subvention plan.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6 animate-fade-in-up" style={{ animationDelay: '350ms' }}>
              {/* Primary - Block Under 30:70 Plan */}
              <Button
                onClick={() => tourGate.run()}
                className="text-base sm:text-lg px-8 py-6 rounded-lg font-semibold shadow-gold h-auto"
                style={{
                  background: 'linear-gradient(135deg, hsl(35, 65%, 45%), hsl(42, 65%, 55%))',
                  color: '#fff',
                  border: 'none',
                }}
              >
                Block Under 30:70 Plan
                <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
              </Button>

              {/* Secondary - See How 30:70 Works */}
              <Button
                onClick={() => setPlanModalOpen(true)}
                variant="outline"
                className="text-base sm:text-lg px-8 py-6 rounded-lg font-medium h-auto"
                style={{
                  background: 'rgba(0, 0, 0, 0.25)',
                  backdropFilter: 'blur(12px)',
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                  color: '#fff',
                }}
              >
                <Info className="w-5 h-5 mr-2" aria-hidden="true" />
                See How 30:70 Works
              </Button>
            </div>

            {/* Authority link */}
            <a
              href="/nahar-chandi-valley-pricing-guide"
              className="inline-block text-white/50 hover:text-accent-light text-sm transition-colors underline underline-offset-4 decoration-white/20 hover:decoration-accent-light animate-fade-in-up"
              style={{ animationDelay: '400ms' }}
            >
              See full 30:70 plan terms &amp; MahaRERA details
            </a>
          </div>
        </div>
      </div>

      {/* Frosted Glass USP Bar */}
      <div className="relative z-30 w-full animate-fade-in-up" style={{ animationDelay: '500ms' }}>
        <div
          className="mx-4 md:mx-8 mb-4 px-6 py-4 rounded-2xl"
          style={{
            background: 'rgba(255, 255, 255, 0.06)',
            backdropFilter: 'blur(24px) saturate(150%)',
            WebkitBackdropFilter: 'blur(24px) saturate(150%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10">
            {usps.map((usp, i) => (
              <div key={i} className="flex items-center gap-3">
                <usp.icon className="w-5 h-5 flex-shrink-0" style={{ color: 'hsl(42, 65%, 65%)', strokeWidth: 1.5 }} />
                <span
                  className="text-sm md:text-[15px] font-medium tracking-wide text-white/90"
                  style={{ fontFamily: "'DM Sans', 'Inter', sans-serif" }}
                >
                  {usp.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Footer Bar — 5 columns desktop, 3+2 grid mobile */}
      <div
        className="relative z-30 w-full"
        style={{
          background: 'rgba(0, 0, 0, 0.92)',
          backdropFilter: 'blur(16px)',
          borderTop: '1px solid rgba(255, 255, 255, 0.06)',
        }}
      >
        <div className="container mx-auto px-4 py-4">
          {/* Mobile: 3 cols top, 2 cols bottom */}
          <div className="grid grid-cols-3 gap-x-3 gap-y-3 sm:hidden">
            {stats.slice(0, 3).map((stat, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                <span
                  className="text-[10px] font-medium uppercase tracking-[0.12em] mb-0.5"
                  style={{ color: 'hsl(42, 50%, 60%)' }}
                >
                  {stat.label}
                </span>
                <span
                  className="text-[12px] font-semibold text-white/95 leading-tight"
                  style={{ fontFamily: "'DM Sans', 'Inter', sans-serif" }}
                >
                  {stat.value}
                </span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-3 sm:hidden mt-3 pt-3 border-t border-white/5">
            {stats.slice(3).map((stat, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                <span
                  className="text-[10px] font-medium uppercase tracking-[0.12em] mb-0.5"
                  style={{ color: 'hsl(42, 50%, 60%)' }}
                >
                  {stat.label}
                </span>
                <span
                  className="text-[12px] font-semibold text-white/95 leading-tight"
                  style={{ fontFamily: "'DM Sans', 'Inter', sans-serif" }}
                >
                  {stat.value}
                </span>
              </div>
            ))}
          </div>

          {/* Desktop/tablet: single row, divided */}
          <div className="hidden sm:flex items-center justify-center gap-6 md:gap-10 lg:gap-14">
            {stats.map((stat, i) => (
              <div key={i} className="flex items-center gap-3 text-center sm:text-left">
                <span
                  className="text-[11px] font-medium uppercase tracking-[0.15em]"
                  style={{ color: 'hsl(42, 50%, 60%)' }}
                >
                  {stat.label}
                </span>
                <span
                  className="text-sm md:text-[15px] font-semibold text-white/95 whitespace-nowrap"
                  style={{ fontFamily: "'DM Sans', 'Inter', sans-serif" }}
                >
                  {stat.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Registration Modal for primary CTA */}
      <RegistrationModal
        isOpen={tourGate.modalOpen}
        onClose={tourGate.closeModal}
        source="contact"
        title="Block Under 30:70 Plan"
        subtitle="Reserve your residence under the launch-phase subvention scheme."
      />

      {/* 30:70 Plan Explainer Modal */}
      <PlanExplainerModal
        isOpen={planModalOpen}
        onClose={() => setPlanModalOpen(false)}
        onBlockUnit={() => tourGate.run()}
      />
    </section>
  );
};

export default Hero;
